import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class GlobalFilterService {
  protected filters: any = {
    queryParams: {},
    requestParams: {},
  };
  constructor(protected http: HttpClient) { }
  protected widgetRefreshSubject = new Subject<string>();
  protected widgetId: string | null = null;
  protected filterSubject = new Subject<any>();
  protected currentSelectedFilter = new Subject<any>();
  protected selectedFilters: { [key: string]: any } = {};

  filter$ = this.filterSubject.asObservable();
  currentFilter$ = this.currentSelectedFilter.asObservable();
  widgetRefresh$ = this.widgetRefreshSubject.asObservable();
  activeFilter!: any;
  dropdownOptions!: { [key: string]: string[] };

  private normalizeForCompare(value: any): any {
    if (Array.isArray(value)) {
      return value
        .map((v) => this.normalizeForCompare(v))
        .sort((a, b) => JSON.stringify(a).localeCompare(JSON.stringify(b)));
    }

    if (value && typeof value === 'object') {
      return Object.keys(value)
        .sort()
        .reduce((acc: any, k: string) => {
          acc[k] = this.normalizeForCompare(value[k]);
          return acc;
        }, {});
    }

    return value;
  }

  private areValuesEqual(a: any, b: any): boolean {
    return JSON.stringify(this.normalizeForCompare(a)) === JSON.stringify(this.normalizeForCompare(b));
  }

  protected cloneValue(value: any): any {
    if (Array.isArray(value)) return value.map((v) => this.cloneValue(v));
    if (value && typeof value === 'object') return { ...value };
    return value;
  }

  protected normalizeStoredFilterValue(value: any): any {
    if (!Array.isArray(value)) {
      return this.cloneValue(value);
    }

    if (value.length === 0) {
      return [];
    }

    if (value.length === 1) {
      return this.cloneValue(value[0]);
    }

    return this.cloneValue(value);
  }

  isSelected(key: string, value: any): boolean {
    return this.areValuesEqual(this.selectedFilters[key], value);
  }
  updateFilterSelection(
    keyOrKeys: string | string[],
    values: any[] | any,
    isQueryParam = false,
    widgetId?: string,
    emit: boolean = true,
    filterListener?: string | string[],
    listenerExtraParams?: { [widgetId: string]: { [key: string]: any } },
    storeRawValue = false,
  ): void {
    const keys = Array.isArray(keyOrKeys)
      ? keyOrKeys
      : (keyOrKeys || '')
        .split(',')
        .map((k) => k.trim())
        .filter((k) => k);

    keys.forEach((key) => {
      const normalizedValue = storeRawValue
        ? values
        : (Array.isArray(values) ? values : [values]);
      const valueToStore = storeRawValue
        ? this.cloneValue(normalizedValue)
        : this.normalizeStoredFilterValue(normalizedValue);
      this.selectedFilters[key] = storeRawValue
        ? this.cloneValue(normalizedValue)
        : this.cloneValue(Array.isArray(normalizedValue) ? normalizedValue[0] : normalizedValue);

      if (isQueryParam) {
        this.filters.queryParams[key] = valueToStore;
      } else {
        this.filters.requestParams[key] = valueToStore;
      }
    });
    this.widgetId = widgetId || null;
    if (emit) {
      const filterData = {
        ...this.filters,
        widgetId: this.widgetId,
        changedFilterKey: keys.length === 1 ? keys[0] : keys,
        filterListener,
        ...(listenerExtraParams ? { listenerExtraParams } : {}),
      };
      this.filterSubject.next(filterData);
      this.currentSelectedFilter.next({ key: keys });
    }
  }

   requestWidgetRefresh(widgetId: string): void {
    this.widgetRefreshSubject.next(widgetId);
  }

  resetFilterSelection(
    key: string,
    isQueryParam = false,
    filterListener?: string | string[],
  ): void {
    delete this.selectedFilters[key];
    if (isQueryParam) {
      delete this.filters.queryParams[key];
    } else {
      delete this.filters.requestParams[key];
    }
    // Forward filterListener so only targeted charts reload (not ALL charts)
    this.filterSubject.next({
      ...this.filters,
      widgetId: this.widgetId,
      ...(filterListener ? { filterListener } : {}),
    });
  }

  deleteKey(key: string, isQueryParam = false): void {
    delete this.selectedFilters[key];
    if (isQueryParam) {
      delete this.filters.queryParams[key];
    } else {
      delete this.filters.requestParams[key];
    }
  }

  getCurrentFilters(): any {
    return this.filters;
  }

  getNavigationFiltersForWidget(widgetId?: string): any | undefined {
    if (!widgetId) return undefined;

    const filters = this.getCurrentFilters();
    const navigationRequestParams = filters?.navigationRequestParams;
    if (!navigationRequestParams || typeof navigationRequestParams !== 'object') {
      return undefined;
    }

    const listeners = this.normalizeFilterListener(filters?.navigationFilterListener);
    if (!listeners.length || !listeners.includes(widgetId)) {
      return undefined;
    }

    return {
      ...filters,
      requestParams: {
        ...(filters?.requestParams || {}),
        ...navigationRequestParams,
      },
      widgetId: null,
      filterListener: listeners,
      sourceWidgetId: filters?.navigationSourceWidgetId || filters?.sourceWidgetId,
    };
  }

  updateAllFilters(newFilters: any): void {
    this.filters = newFilters;
    this.selectedFilters = {};
    this.filterSubject.next(this.filters);
    this.currentSelectedFilter.next({});
  }

  clearAllFilters() {
    this.filters = {
      queryParams: {},
      requestParams: {},
    };
    this.widgetId = null;
    this.filterSubject.next({
      ...this.filters,
      widgetId: null,
      changedFilterKey: null,
      resetFilterControls: true,
    });
    this.selectedFilters = {};
    this.currentSelectedFilter.next('');
  }

  emitCurrentFilters(): void {
    this.filterSubject.next({
      ...this.filters,
      widgetId: null,
    });
  }

  emitRefreshWithoutPayload(filterListener?: string | string[], widgetId: string | null = null): void {
    this.filterSubject.next({
      queryParams: {},
      requestParams: {},
      widgetId,
      refreshAll: !filterListener,
      ...(filterListener ? { filterListener } : {}),
    });
  }

  initialTrigger() {
    // skipReload: true tells charts NOT to fire an API call for this emission.
    // Charts already load themselves via ngAfterViewInit — this trigger is only
    // for filter dropdowns to populate, not for charts to reload.
    this.filterSubject.next({
      queryParams: {},
      requestParams: {},
      widgetId: null,
      skipReload: true,
    });
  }

  private normalizeFilterListener(rawValue: unknown): string[] {
    const rawList = Array.isArray(rawValue)
      ? rawValue
      : typeof rawValue === 'string'
        ? rawValue.split(',')
        : [];

    return rawList
      .map((item) => String(item || '').trim())
      .filter((item) => !!item);
  }

  downloadExcel(apiUrl: string, filterPayload: any): Observable<Blob> {
    return this.http.post(apiUrl, filterPayload, {
      responseType: 'blob',
      withCredentials: false, // 👈 STOPS interceptor from reading undefined cookie
    });
  }

  buildPayload(config: any, filters: any[]): any {
    if (config?.skipDefaultPayload) {
      // If customPayload is explicitly true, or missing/null/undefined, return empty object.
      // Otherwise, return the customPayload object itself.
      return config?.customPayload === true ? {} : config?.customPayload || {};
    }

    const selectedRequestData = this.buildRequestDataFromWhereFilters(filters);
    const seededRequestData = this.buildRequestDataFromConfiguredRequestParams(config);
    const hasSelectedFilters = Object.keys(selectedRequestData).length > 0;
    const defaultRequestData =
      config?.defaultRequestData || config?.defaultRequesData || config?.defaultPayload || {};
    const includeFiltersInRequestData = config?.includeFiltersInRequestData === true;

    const requestData = {
      ...(!hasSelectedFilters ? defaultRequestData : {}),
      ...seededRequestData,
      ...selectedRequestData,
      ...(includeFiltersInRequestData ? { filters: filters } : {}),
    };

    return {
      requestData,
      responseList: [],
      entity: config?.entity || 'default_entity',
      workflowId: config?.workflowId || 'default_workflow',
      endpoints: [
        {
          functionName: config?.functionName || 'DefaultService',
          version: config?.version || '1.0',
        },
      ],
      communicationType: 'rest',
      version: config?.version || '1.0',
    };
  }

  private buildRequestDataFromWhereFilters(filters: any[]): Record<string, any> {
    if (!Array.isArray(filters)) {
      return {};
    }

    return filters.reduce<Record<string, any>>((requestData, filter) => {
      const key = filter?.attributeName;
      if (!key) {
        return requestData;
      }

      const values = Array.isArray(filter?.attributeValues)
        ? filter.attributeValues.filter((value: any) => value !== null && value !== undefined && value !== '')
        : [];

      if (!values.length) {
        return requestData;
      }

      requestData[key] = values.length === 1 ? values[0] : values;
      return requestData;
    }, {});
  }

  private buildRequestDataFromConfiguredRequestParams(config: any): Record<string, any> {
    const requestParams = this.filters?.requestParams || {};
    const requestKeys: string[] = Array.isArray(config?.dropdownRequestPayloadKeys)
      ? config.dropdownRequestPayloadKeys as string[]
      : [];
    const requestKeyMap = config?.dropdownRequestKeyMap || {};

    return requestKeys.reduce((requestData: Record<string, any>, key: string) => {
      const requestParamValue = requestParams?.[key];
      if (
        requestParamValue === undefined ||
        requestParamValue === null ||
        requestParamValue === '' ||
        (Array.isArray(requestParamValue) && requestParamValue.length === 0)
      ) {
        return requestData;
      }

      const targetKey = String(requestKeyMap?.[key] || key).trim();
      if (!targetKey) {
        return requestData;
      }

      requestData[targetKey] = requestParamValue;
      return requestData;
    }, {} as Record<string, any>);
  }
}
